import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { GoogleReCaptchaEnv } from '@qaroni-core/config/apis/google-recaptcha/google-recaptcha.config';
import { ReCaptchaConfig } from '@qaroni-core/types/re-captcha-config/re-captcha-config';

interface GreCaptcha {
  render(container: HTMLElement, parameters: object): number;
}

declare global {
  interface Window {
    grecaptcha: GreCaptcha;
    gReCaptchaOnLoadCallback: () => void;
  }
}
@Component({
  selector: 'qaroni-grecaptcha',
  templateUrl: './grecaptcha.component.html',
  styleUrls: ['./grecaptcha.component.scss'],
  standalone: false,
})
export class GrecaptchaComponent implements OnInit, OnDestroy {
  readonly config = input<ReCaptchaConfig>({});

  readonly captchaResponse = output<string>();
  readonly captchaExpired = output<void>();

  @ViewChild('recaptcha', { static: true }) recaptchaElement!: ElementRef;

  ngZone = inject(NgZone);

  private widgetId = 0;
  private tagScriptId = 'recaptcha-js-sdk';

  ngOnInit(): void {
    if (this.hasReCaptcha) {
      this.registerReCaptchaCallback();
      this.addReCaptchaScript();
    }
  }

  ngOnDestroy(): void {
    const script = document.getElementById(this.tagScriptId);
    if (script) {
      script.parentNode.removeChild(script);
    }
  }

  get hasReCaptcha(): boolean {
    if (GoogleReCaptchaEnv.scriptSrc && GoogleReCaptchaEnv.sitekey) {
      return true;
    }
    return false;
  }

  private registerReCaptchaCallback(): void {
    window.gReCaptchaOnLoadCallback = () => {
      const config = {
        ...this.config(),
        sitekey: GoogleReCaptchaEnv.sitekey,
        callback: this.onSuccess.bind(this),
        'expired-callback': this.onExpired.bind(this),
      };
      this.widgetId = this.renderReCaptcha(
        this.recaptchaElement.nativeElement,
        config
      );
    };
  }

  private renderReCaptcha(element: HTMLElement, config): number {
    return window.grecaptcha.render(element, config);
  }

  private addReCaptchaScript(): void {
    if (document.getElementById(this.tagScriptId)) {
      this.registerReCaptchaCallback();
      return;
    }
    const script = document.createElement('script');
    script.id = this.tagScriptId;
    script.src = GoogleReCaptchaEnv.scriptSrc;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }

  public onSuccess(token: string): void {
    this.ngZone.run(() => {
      this.captchaResponse.emit(token);
    });
  }

  public onExpired(): void {
    this.ngZone.run(() => {
      this.captchaExpired.emit();
    });
  }
}
